import { apiFetch } from './client';

// Chat (mobile-app §7) — the admin SPA is a first-class chat surface (§7.6),
// wired to the SAME internal/services/chat backend the mobile app uses. Mirrors
// the server shapes in internal/api/chat.go + chat_ws.go.

export interface Conversation {
  id: string;
  kind: 'direct' | 'group';
  title: string;
  created_by_user_id: string | null;
  last_message_at: string | null;
  unread: number;
  member_count: number;
}

export interface ConversationsPage {
  conversations: Conversation[];
  total: number;
  limit: number;
  offset: number;
}

export interface Message {
  id: string;
  sender_user_id: string | null;
  sender_display_name: string;
  body: string;
  reply_to_id: string | null;
  created_at: string; // RFC3339
}

export interface Participant {
  id: string;
  user_id: string | null;
  member_id: string | null;
  display_name: string;
  email: string;
  has_account: boolean;
  joined_at: string;
}

// AddressBookMember is one roster person in the new-chat people picker. A member
// is anyone in the club roster (athlete / guardian / leader). has_account tells
// us whether they've linked a login yet — when false they can still be picked,
// they just join the thread once they sign in. Mirrors addressBookMemberItem in
// internal/api/chat.go.
//
// guardians is the dependent's family-relationship guardians (from
// members.member_guardian); the SPA shows them as an inline expansion under
// the athlete row. Omitted when the row has no guardian links; never nested
// inside another guardians[] (no recursion).
export interface AddressBookMember {
  member_id: string;
  display_name: string;
  email: string;
  team_role: string;
  has_account: boolean;
  // Only present on a guardian row: how they relate to the dependent
  // (parent / legal_guardian / …). Omitted on top-level members and when the
  // link's relationship is unknown.
  relationship?: string;
  guardians?: AddressBookMember[];
}

// AddressBookTeam is one collapsible group in the picker. team_id is null for
// the synthetic "no team" bucket.
export interface AddressBookTeam {
  team_id: string | null;
  name: string;
  members: AddressBookMember[];
}

export async function listAddressBook(slug: string, token: string | null): Promise<AddressBookTeam[]> {
  const res = await apiFetch<{ teams: AddressBookTeam[] }>(
    `/api/v1/organizations/${slug}/chat/address-book`,
    { token },
  );
  return res.teams ?? [];
}

// getChatUnread returns the caller's total unread chat-message count for this
// org — a cheap single-number query that drives the header chat badge. A per-org
// counterpart to /me/chat/summary. Mirrors h.unreadCount in internal/api/chat.go.
export async function getChatUnread(slug: string, token: string | null): Promise<number> {
  const res = await apiFetch<{ unread: number }>(
    `/api/v1/organizations/${slug}/chat/unread`,
    { token },
  );
  return res.unread ?? 0;
}

export async function listConversations(slug: string, token: string | null): Promise<Conversation[]> {
  const res = await apiFetch<{ conversations: Conversation[] }>(
    `/api/v1/organizations/${slug}/chat/conversations`,
    { token },
  );
  return res.conversations ?? [];
}

// listConversationsPage is the paged counterpart used by the Chat list. The
// admin SPA renders numbered pages on top of this; the mobile app's inbox
// can keep using listConversations until it migrates.
export async function listConversationsPage(
  slug: string,
  token: string | null,
  limit: number,
  offset: number,
): Promise<ConversationsPage> {
  const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const res = await apiFetch<ConversationsPage>(
    `/api/v1/organizations/${slug}/chat/conversations?${qs.toString()}`,
    { token },
  );
  return {
    conversations: res.conversations ?? [],
    total: res.total ?? 0,
    limit: res.limit ?? limit,
    offset: res.offset ?? offset,
  };
}

export async function renameConversation(
  slug: string,
  conversationId: string,
  title: string,
  token: string | null,
): Promise<void> {
  await apiFetch(`/api/v1/organizations/${slug}/chat/conversations/${conversationId}`, {
    method: 'PATCH',
    body: { title },
    token,
  });
}

export async function deleteConversation(
  slug: string,
  conversationId: string,
  token: string | null,
): Promise<void> {
  await apiFetch(`/api/v1/organizations/${slug}/chat/conversations/${conversationId}`, {
    method: 'DELETE',
    token,
  });
}

export async function addParticipants(
  slug: string,
  conversationId: string,
  memberIds: string[],
  token: string | null,
): Promise<{ added: number }> {
  return apiFetch(
    `/api/v1/organizations/${slug}/chat/conversations/${conversationId}/participants`,
    { method: 'POST', body: { member_ids: memberIds }, token },
  );
}

export async function removeParticipant(
  slug: string,
  conversationId: string,
  participantId: string,
  token: string | null,
): Promise<void> {
  await apiFetch(
    `/api/v1/organizations/${slug}/chat/conversations/${conversationId}/participants/${participantId}`,
    { method: 'DELETE', token },
  );
}

export async function startConversation(
  slug: string,
  body: { kind: 'direct' | 'group'; title?: string; participant_member_ids: string[] },
  token: string | null,
): Promise<{ id: string }> {
  return apiFetch(`/api/v1/organizations/${slug}/chat/conversations`, { method: 'POST', body, token });
}

export async function getMessages(
  slug: string,
  conversationId: string,
  token: string | null,
  before?: string,
): Promise<Message[]> {
  const q = before ? `?before=${encodeURIComponent(before)}` : '';
  const res = await apiFetch<{ messages: Message[] }>(
    `/api/v1/organizations/${slug}/chat/conversations/${conversationId}/messages${q}`,
    { token },
  );
  return res.messages ?? [];
}

export async function sendMessage(
  slug: string,
  conversationId: string,
  bodyText: string,
  token: string | null,
): Promise<{ id: string }> {
  return apiFetch(`/api/v1/organizations/${slug}/chat/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: { body: bodyText },
    token,
  });
}

export async function listParticipants(
  slug: string,
  conversationId: string,
  token: string | null,
): Promise<Participant[]> {
  const res = await apiFetch<{ participants: Participant[] }>(
    `/api/v1/organizations/${slug}/chat/conversations/${conversationId}/participants`,
    { token },
  );
  return res.participants ?? [];
}

export async function markRead(slug: string, conversationId: string, token: string | null): Promise<void> {
  await apiFetch(`/api/v1/organizations/${slug}/chat/conversations/${conversationId}/read`, {
    method: 'POST',
    token,
  });
}

// fetchWsTicket gets the short-lived signed ticket the WebSocket upgrade needs
// (browsers can't set a Bearer header on a ws handshake).
export async function fetchWsTicket(slug: string, token: string | null): Promise<string> {
  const res = await apiFetch<{ ticket: string }>(
    `/api/v1/organizations/${slug}/chat/ws-ticket`,
    { method: 'POST', token },
  );
  return res.ticket;
}
